import { db as firebaseDb } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ConditionAssessment,
  AssessmentMethod,
  getConditionGrade,
} from '@easydone/shared';

/**
 * Create a condition assessment
 */
export async function createConditionAssessment(
  orgId: string,
  data: {
    assetId: string;
    flocId?: string;
    score: number;
    assessmentMethod: AssessmentMethod;
    findings?: string;
    recommendations?: string;
    photos?: string[];
    componentScores?: Record<string, number>;
    inspectionId?: string;
    maintenanceId?: string;
  },
  userId: string,
  userName: string
): Promise<ConditionAssessment> {
  const grade = getConditionGrade(data.score);

  const assessmentsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'conditionAssessments'
  );

  const assessmentData = {
    orgId,
    assetId: data.assetId,
    flocId: data.flocId || null,
    score: data.score,
    grade,
    assessedAt: serverTimestamp(),
    assessedBy: userId,
    assessedByName: userName,
    assessmentMethod: data.assessmentMethod,
    inspectionId: data.inspectionId || null,
    maintenanceId: data.maintenanceId || null,
    findings: data.findings || null,
    recommendations: data.recommendations || null,
    photos: data.photos || [],
    componentScores: data.componentScores || null,
    metadata: {},
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(assessmentsRef, assessmentData);

  // Update asset's condition score
  const assetRef = doc(firebaseDb, 'organizations', orgId, 'assets', data.assetId);
  await updateDoc(assetRef, {
    conditionScore: data.score,
    conditionGrade: grade,
    lastConditionUpdate: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const snapshot = await getDoc(docRef);
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as ConditionAssessment;
}

/**
 * Get condition assessments for an asset
 */
export async function getAssetConditionHistory(
  orgId: string,
  assetId: string,
  limitCount: number = 50
): Promise<ConditionAssessment[]> {
  const assessmentsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'conditionAssessments'
  );

  const q = query(
    assessmentsRef,
    where('assetId', '==', assetId),
    orderBy('assessedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as ConditionAssessment));
}

/**
 * Get condition assessments for a FLOC
 */
export async function getFlocConditionHistory(
  orgId: string,
  flocId: string,
  limitCount: number = 50
): Promise<ConditionAssessment[]> {
  const assessmentsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'conditionAssessments'
  );

  const q = query(
    assessmentsRef,
    where('flocId', '==', flocId),
    orderBy('assessedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as ConditionAssessment));
}

/**
 * Get a single condition assessment
 */
export async function getConditionAssessment(
  orgId: string,
  assessmentId: string
): Promise<ConditionAssessment | null> {
  const assessmentRef = doc(
    firebaseDb,
    'organizations',
    orgId,
    'conditionAssessments',
    assessmentId
  );
  const snapshot = await getDoc(assessmentRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as ConditionAssessment;
}

/**
 * Update condition assessment
 */
export async function updateConditionAssessment(
  orgId: string,
  assessmentId: string,
  data: Partial<ConditionAssessment>
): Promise<void> {
  const assessmentRef = doc(
    firebaseDb,
    'organizations',
    orgId,
    'conditionAssessments',
    assessmentId
  );

  await updateDoc(assessmentRef, data);
}

/**
 * Calculate condition trend for an asset
 */
export async function getAssetConditionTrend(
  orgId: string,
  assetId: string
): Promise<{
  currentScore: number;
  previousScore?: number;
  trend: 'improving' | 'stable' | 'degrading';
  trendPercentage?: number;
  assessmentCount: number;
}> {
  const assessments = await getAssetConditionHistory(orgId, assetId, 10);

  if (assessments.length === 0) {
    return {
      currentScore: 0,
      trend: 'stable',
      assessmentCount: 0,
    };
  }

  const currentScore = assessments[0].score;
  const previousScore = assessments.length > 1 ? assessments[1].score : undefined;

  let trend: 'improving' | 'stable' | 'degrading' = 'stable';
  let trendPercentage: number | undefined;

  if (previousScore !== undefined) {
    const diff = currentScore - previousScore;
    trendPercentage = (diff / previousScore) * 100;

    if (diff > 2) {
      trend = 'improving';
    } else if (diff < -2) {
      trend = 'degrading';
    }
  }

  return {
    currentScore,
    previousScore,
    trend,
    trendPercentage,
    assessmentCount: assessments.length,
  };
}

/**
 * Get assets with low condition scores
 */
export async function getAssetsWithLowCondition(
  orgId: string,
  threshold: number = 60,
  limitCount: number = 50
): Promise<string[]> {
  // Note: This requires a composite index on orgId + conditionScore
  const assetsRef = collection(firebaseDb, 'organizations', orgId, 'assets');
  const q = query(
    assetsRef,
    where('orgId', '==', orgId),
    where('conditionScore', '<=', threshold),
    orderBy('conditionScore', 'asc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.id);
}
